import { Badge, Box, Card, Group, Text, ThemeIcon } from "@mantine/core";
import type { ChargeContract } from "../../../../apis/chargeContract";
import { ComponentTypeIcon, componentTypeLabel, orderTypeLabel } from "../../../../components/billing/ComponentTypeIcon";
import { EndCustomerRollupPreview } from "../../../../components/billing/EndCustomerRollupPreview";
import { MoneyCell } from "../../../../components/billing/MoneyCell";
import { formatDate, formatMoney, formatPercent } from "../../../../utils/format";

type Component = ChargeContract["components"][number];

const freqLabel: Record<string, string> = { MONTHLY: "сар", QUARTERLY: "улирал", ANNUAL: "жил" };

interface Props {
  component: Component;
  contractCurrency: string;
}

/** A single charge component rendered per its `componentType` (spec §2.2). */
export function ComponentCard({ component: c, contractCurrency }: Props) {
  const enabled = c.isEnabled !== false;
  const cur = c.currency ?? contractCurrency ?? "MNT";
  const freq = (f?: string | null) => (f ? `/ ${freqLabel[f] ?? f}` : "");

  // Right-aligned headline figure per component type.
  const keyFigure = (): React.ReactNode => {
    switch (c.componentType) {
      case "RECURRING_SUBSCRIPTION":
        return (
          <Group gap={4} wrap="nowrap" justify="flex-end" align="baseline">
            <MoneyCell value={c.recurringAmount} currency={cur} fw={600} />
            <Text size="xs" c="dimmed">{freq(c.recurringFrequency)}</Text>
          </Group>
        );
      case "TRANSACTION_FEE":
        return (
          <Text fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatPercent(Number(c.txFeeRate) * 100)}{" "}
            <Text span size="xs" c="dimmed">/ биелсэн захиалга</Text>
          </Text>
        );
      case "END_CUSTOMER_RECURRING":
        return (
          <Group gap={4} wrap="nowrap" justify="flex-end" align="baseline">
            <MoneyCell value={c.ecRecurringAmount} currency={cur} fw={600} />
            <Text size="xs" c="dimmed">/ merchant {freq(c.ecRecurringFrequency)}</Text>
          </Group>
        );
      case "ONE_TIME_PURCHASE":
        return (
          <Group gap={4} wrap="nowrap" justify="flex-end" align="baseline">
            <MoneyCell value={c.oneTimeAmount} currency={cur} fw={600} />
            <Text size="xs" c="dimmed">/ нэг удаа</Text>
          </Group>
        );
      default:
        return <Text size="sm" c="dimmed">Үнэгүй / Тусгай</Text>;
    }
  };

  // The "extra" detail line (real-world calculation / terms) per spec §2.2.
  const extra = (): React.ReactNode => {
    switch (c.componentType) {
      case "RECURRING_SUBSCRIPTION":
        return (
          <Text size="xs" c="dimmed">
            {c.recurringAnchorDay
              ? `Сар бүрийн ${c.recurringAnchorDay}-нд нэхэмжилнэ`
              : "Сар бүр нэхэмжилнэ"}
          </Text>
        );
      case "TRANSACTION_FEE": {
        const types = c.txFeeApplicableOrderTypes?.length
          ? ` · ${c.txFeeApplicableOrderTypes.map(orderTypeLabel).join(", ")}`
          : "";
        return (
          <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
            Доод {formatMoney(c.txFeeMinAmount, cur)} · Дээд {formatMoney(c.txFeeMaxAmount, cur)}{types}
          </Text>
        );
      }
      case "END_CUSTOMER_RECURRING":
        return (
          <Box mt="xs">
            {c.ecCollectionMode && (
              <Text size="xs" c="dimmed" mb={4}>{c.ecCollectionMode}</Text>
            )}
            <EndCustomerRollupPreview
              customerCount={c.ecActiveMerchantCount ?? 0}
              perCustomerAmount={Number(c.ecRecurringAmount ?? 0)}
              currency={cur}
              frequency={(c.ecRecurringFrequency as "MONTHLY" | "QUARTERLY" | "ANNUAL") ?? "MONTHLY"}
            />
          </Box>
        );
      case "ONE_TIME_PURCHASE":
        return c.oneTimeLicenseExpiresAt ? (
          <Text size="xs" c="dimmed">Лиценз дуусах: {formatDate(c.oneTimeLicenseExpiresAt)}</Text>
        ) : (
          <Text size="xs" c="dimmed">Нэг удаагийн нэхэмжлэхэд орно</Text>
        );
      case "FREE_OR_CUSTOM":
        return c.customTerms ? <Text size="xs" c="dimmed">{c.customTerms}</Text> : null;
      default:
        return null;
    }
  };

  return (
    <Card withBorder radius="md" padding="md" opacity={enabled ? 1 : 0.6}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <ThemeIcon variant="light" color={enabled ? "brand" : "gray"} size={36} radius="md">
            <ComponentTypeIcon type={c.componentType} size={18} />
          </ThemeIcon>
          <Box>
            <Text fw={600}>{c.label ?? componentTypeLabel(c.componentType)}</Text>
            <Text size="xs" c="dimmed">{componentTypeLabel(c.componentType)}</Text>
          </Box>
        </Group>
        <Group gap="sm" wrap="nowrap" align="center">
          {keyFigure()}
          <Badge variant="light" color={enabled ? "green" : "gray"} radius="sm">
            {enabled ? "Идэвхтэй" : "Идэвхгүй"}
          </Badge>
        </Group>
      </Group>
      <Box mt="sm" ml={48}>{extra()}</Box>
    </Card>
  );
}
